import React, { useEffect, useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  ArrowUpRight,
  Coffee,
  GitBranch,
  Globe2,
  Mail,
  Settings,
} from "lucide-react";
import defaults from "../data/site-defaults.json";
import "./styles.css";

const labels = {
  zh: {
    navAbout: "关于",
    navWriting: "写作",
    navProjects: "项目",
    navSupport: "支持",
    admin: "后台",
    heroKicker: "AI / 产品 / 自媒体 / 摇滚",
    readBlog: "阅读博客",
    viewProjects: "查看项目",
    supportMe: "赞助支持",
    follow: "关注与联系",
    aboutTitle: "持续学习，持续发声",
    writingTitle: "写作入口",
    featured: "精选",
    latest: "最新",
    allPosts: "进入完整博客",
    projectsTitle: "开源与实验",
    supportTitle: "赞助与打赏",
    sponsorEmpty: "赞助信息和二维码可在后台上传维护。",
    email: "邮件联系",
    adminTitle: "站点后台",
    password: "管理密码",
    login: "进入后台",
    logout: "退出",
    save: "保存配置",
    fetchRepo: "读取 GitHub",
    addProject: "添加项目",
    addPost: "添加文章",
    configSaved: "已保存",
    configFailed: "保存失败",
    repoUrl: "仓库地址",
    name: "名称",
    descZh: "中文描述",
    descEn: "英文描述",
    featuredPost: "精选文章",
    latestPost: "最新文章",
    supportIntro: "支持文案",
    links: "链接",
    projects: "项目",
    posts: "文章",
    support: "赞助",
    now: "现在",
    note1: "用产品经理的方式理解 AI，用创作者的方式记录变化。",
    note2: "写作、开源项目和社交平台会在这里汇合。",
  },
  en: {
    navAbout: "About",
    navWriting: "Writing",
    navProjects: "Projects",
    navSupport: "Support",
    admin: "Admin",
    heroKicker: "AI / Product / Media / Rock",
    readBlog: "Read Blog",
    viewProjects: "View Projects",
    supportMe: "Sponsor",
    follow: "Follow",
    aboutTitle: "Keep Learning. Keep Shipping.",
    writingTitle: "Writing",
    featured: "Featured",
    latest: "Latest",
    allPosts: "Open Blog",
    projectsTitle: "Open Source & Experiments",
    supportTitle: "Sponsorship & Tips",
    sponsorEmpty: "Sponsorship links and QR images can be managed in admin.",
    email: "Email",
    adminTitle: "Site Admin",
    password: "Admin Password",
    login: "Enter Admin",
    logout: "Logout",
    save: "Save",
    fetchRepo: "Fetch GitHub",
    addProject: "Add Project",
    addPost: "Add Post",
    configSaved: "Saved",
    configFailed: "Save failed",
    repoUrl: "Repository URL",
    name: "Name",
    descZh: "Chinese Description",
    descEn: "English Description",
    featuredPost: "Featured Post",
    latestPost: "Latest Post",
    supportIntro: "Support Copy",
    links: "Links",
    projects: "Projects",
    posts: "Posts",
    support: "Support",
    now: "Now",
    note1: "Understanding AI like a product manager, documenting change like a creator.",
    note2: "Writing, open-source projects, and social links meet here.",
  },
};

function detectLang() {
  const stored = localStorage.getItem("qiaomu-lang");
  if (stored === "zh" || stored === "en") return stored;
  return navigator.language?.toLowerCase().startsWith("zh") ? "zh" : "en";
}

function localText(value, lang) {
  if (!value) return "";
  if (typeof value === "string") return value;
  return value[lang] || value.zh || value.en || "";
}

function sortByPriority(items = []) {
  return [...items].sort((a, b) => (a.priority || 99) - (b.priority || 99));
}

async function loadSite() {
  try {
    const response = await fetch("/api/site");
    if (!response.ok) throw new Error("Failed to load site config");
    return await response.json();
  } catch {
    return defaults;
  }
}

function App() {
  const [site, setSite] = useState(defaults);
  const [lang, setLang] = useState(detectLang);
  const [adminOpen, setAdminOpen] = useState(false);

  useEffect(() => {
    loadSite().then(setSite);
  }, []);

  const t = labels[lang];
  const profile = site.profile || defaults.profile;

  function switchLang(next) {
    setLang(next);
    localStorage.setItem("qiaomu-lang", next);
  }

  return (
    <div className="site-shell">
      <Header lang={lang} switchLang={switchLang} t={t} onAdmin={() => setAdminOpen(true)} />
      <main>
        <Hero site={site} lang={lang} t={t} />
        <section id="about" className="section intro-section">
          <div>
            <p className="section-kicker">{t.aboutTitle}</p>
            <p className="body-copy">{localText(profile.bio, lang)}</p>
          </div>
          <div className="notes-panel">
            <p>{t.note1}</p>
            <p>{t.note2}</p>
          </div>
        </section>
        <Writing site={site} lang={lang} t={t} />
        <Projects site={site} lang={lang} t={t} />
        <Support site={site} lang={lang} t={t} />
      </main>
      <Footer site={site} t={t} />
      {adminOpen && <AdminPanel site={site} setSite={setSite} lang={lang} t={t} onClose={() => setAdminOpen(false)} />}
    </div>
  );
}

function Header({ lang, switchLang, t, onAdmin }) {
  return (
    <header className="topbar">
      <a className="brand-mark" href="#top" aria-label="qiaomu.ai">
        <span>向阳乔木</span>
        <b>qiaomu.ai</b>
      </a>
      <nav>
        <a href="#about">{t.navAbout}</a>
        <a href="#writing">{t.navWriting}</a>
        <a href="#projects">{t.navProjects}</a>
        <a href="#support">{t.navSupport}</a>
      </nav>
      <div className="top-actions">
        <button className="icon-button" onClick={() => switchLang(lang === "zh" ? "en" : "zh")} aria-label="Switch language">
          <Globe2 size={17} />
          <span>{lang === "zh" ? "EN" : "中"}</span>
        </button>
        <button className="icon-button ghost" onClick={onAdmin} aria-label={t.admin}>
          <Settings size={17} />
        </button>
      </div>
    </header>
  );
}

function Hero({ site, lang, t }) {
  const profile = site.profile || defaults.profile;
  const links = (site.links || []).filter((link) => link.visible !== false);
  return (
    <section id="top" className="hero">
      <div className="hero-card">
        <div className="hero-copy">
          <p className="hero-kicker">{t.heroKicker}</p>
          <h1>{profile.name}</h1>
          <p className="hero-tagline">{localText(profile.tagline, lang)}</p>
          <div className="hero-actions">
            <a className="button primary" href="https://blog.qiaomu.ai">
              {t.readBlog}<ArrowUpRight size={16} />
            </a>
            <a className="button" href="#projects">{t.viewProjects}</a>
            <a className="button" href="#support">{t.supportMe}</a>
          </div>
        </div>
        <div className="hero-aside">
          <p>{t.now}</p>
          <strong>AI Builder · Product Manager · Media</strong>
          <span>qiaomu.ai</span>
        </div>
      </div>
      <div className="social-strip" aria-label={t.follow}>
        {links.map((link) => (
          <a key={link.id || link.url} href={link.url} target={link.url.startsWith("mailto:") ? undefined : "_blank"} rel="noreferrer">
            {link.label}<ArrowUpRight size={14} />
          </a>
        ))}
      </div>
    </section>
  );
}

function Writing({ site, lang, t }) {
  const featured = site.posts?.featured || [];
  const latest = site.posts?.latest || [];
  return (
    <section id="writing" className="section">
      <div className="section-heading">
        <div>
          <p className="section-kicker">{t.writingTitle}</p>
          <h2>{lang === "zh" ? "文章与记录" : "Posts and Notes"}</h2>
        </div>
        <a className="text-link" href="https://blog.qiaomu.ai" target="_blank" rel="noreferrer">
          {t.allPosts}<ArrowUpRight size={15} />
        </a>
      </div>
      <div className="post-grid">
        <PostColumn title={t.featured} posts={featured} lang={lang} />
        <PostColumn title={t.latest} posts={latest} lang={lang} />
      </div>
    </section>
  );
}

function PostColumn({ title, posts, lang }) {
  return (
    <div className="post-column">
      <h3>{title}</h3>
      {posts.map((post, index) => (
        <a className="post-item" href={post.url} target="_blank" rel="noreferrer" key={`${post.url}-${index}`}>
          <time>{post.date}</time>
          <strong>{localText(post.title, lang)}</strong>
          <span>{localText(post.excerpt, lang)}</span>
        </a>
      ))}
    </div>
  );
}

function Projects({ site, lang, t }) {
  const projects = sortByPriority(site.projects || []);
  return (
    <section id="projects" className="section">
      <div className="section-heading">
        <div>
          <p className="section-kicker">{t.projectsTitle}</p>
          <h2>{lang === "zh" ? "项目与实验" : "Projects and Experiments"}</h2>
        </div>
        <a className="text-link" href="https://github.com/joeseesun/" target="_blank" rel="noreferrer">
          GitHub<ArrowUpRight size={15} />
        </a>
      </div>
      <div className="project-grid">
        {projects.map((project) => (
          <a className="project-card" href={project.liveUrl || project.repoUrl} target="_blank" rel="noreferrer" key={project.id || project.repoUrl}>
            <div>
              <span className="project-language">{project.language || "Project"}</span>
              <h3>{project.name}</h3>
              <p>{localText(project.description, lang)}</p>
            </div>
            <div className="project-meta">
              <span>{(project.tags || []).slice(0, 3).join(" / ")}</span>
              <span>{project.stars ? `★ ${project.stars}` : "↗"}</span>
            </div>
          </a>
        ))}
      </div>
    </section>
  );
}

function Support({ site, lang, t }) {
  const support = site.support || defaults.support;
  const hasQr = support.qrCodes?.length > 0;
  return (
    <section id="support" className="section support-section">
      <div>
        <p className="section-kicker">{t.supportTitle}</p>
        <h2>{lang === "zh" ? "如果内容对你有帮助" : "If the work helps"}</h2>
        <p className="body-copy">{localText(support.intro, lang)}</p>
        {support.buyMeCoffeeUrl && (
          <a className="button primary" href={support.buyMeCoffeeUrl} target="_blank" rel="noreferrer">
            <Coffee size={18} /> Buy Me a Coffee
          </a>
        )}
      </div>
      <div className="support-board">
        {hasQr ? support.qrCodes.map((qr) => (
          <div className="qr-card" key={qr.id || qr.url}>
            <img src={qr.url} alt={qr.label} />
            <span>{qr.label}</span>
          </div>
        )) : <p>{t.sponsorEmpty}</p>}
      </div>
    </section>
  );
}

function Footer({ site, t }) {
  const email = site.profile?.email || "vista8@gmail.com";
  return (
    <footer className="footer">
      <span>© {new Date().getFullYear()} qiaomu.ai</span>
      <a href={`mailto:${email}`}><Mail size={15} />{t.email}</a>
      <a href="https://github.com/joeseesun/qiaomu-ai-website" target="_blank" rel="noreferrer"><GitBranch size={15} />Source</a>
    </footer>
  );
}

function AdminPanel({ site, setSite, lang, t, onClose }) {
  const [password, setPassword] = useState(sessionStorage.getItem("qiaomu-admin-password") || "");
  const [draft, setDraft] = useState(site);
  const [message, setMessage] = useState("");
  const [authed, setAuthed] = useState(Boolean(sessionStorage.getItem("qiaomu-admin-password")));

  useEffect(() => setDraft(site), [site]);

  async function login(event) {
    event.preventDefault();
    const response = await fetch("/api/admin/session", {
      method: "POST",
      headers: { "Content-Type": "application/json", "x-admin-password": password },
      body: JSON.stringify({}),
    });
    if (response.ok) {
      sessionStorage.setItem("qiaomu-admin-password", password);
      setAuthed(true);
    } else {
      setMessage("Password rejected");
    }
  }

  async function save() {
    const response = await fetch("/api/admin/site", {
      method: "PUT",
      headers: { "Content-Type": "application/json", "x-admin-password": password },
      body: JSON.stringify(draft),
    });
    if (response.ok) {
      setSite(draft);
      setMessage(t.configSaved);
    } else {
      setMessage(t.configFailed);
    }
  }

  async function uploadQr(event, index) {
    const file = event.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    const response = await fetch("/api/upload", {
      method: "POST",
      headers: { "x-admin-password": password },
      body: formData,
    });
    if (!response.ok) {
      setMessage("Upload failed");
      return;
    }
    const result = await response.json();
    updateQrCode(index, { ...draft.support.qrCodes[index], url: result.url });
  }

  async function fetchRepo(index) {
    const repoUrl = draft.projects[index]?.repoUrl;
    if (!repoUrl) return;
    const response = await fetch(`/api/github/repo?url=${encodeURIComponent(repoUrl)}`);
    if (!response.ok) return;
    const repo = await response.json();
    updateProject(index, {
      ...draft.projects[index],
      name: repo.name || draft.projects[index].name,
      language: repo.language || draft.projects[index].language,
      stars: repo.stars || 0,
      description: {
        zh: draft.projects[index].description?.zh || repo.description || "",
        en: draft.projects[index].description?.en || repo.description || "",
      },
      liveUrl: repo.homepage || draft.projects[index].liveUrl,
    });
  }

  function updateProfile(path, value) {
    setDraft((current) => ({
      ...current,
      profile: {
        ...current.profile,
        [path]: value,
      },
    }));
  }

  function updateProfileText(field, language, value) {
    setDraft((current) => ({
      ...current,
      profile: {
        ...current.profile,
        [field]: {
          ...current.profile[field],
          [language]: value,
        },
      },
    }));
  }

  function updateProject(index, nextProject) {
    setDraft((current) => ({
      ...current,
      projects: current.projects.map((project, projectIndex) => projectIndex === index ? nextProject : project),
    }));
  }

  function updateLink(index, nextLink) {
    setDraft((current) => ({
      ...current,
      links: current.links.map((link, linkIndex) => linkIndex === index ? nextLink : link),
    }));
  }

  function updatePost(group, index, nextPost) {
    setDraft((current) => ({
      ...current,
      posts: {
        ...current.posts,
        [group]: current.posts[group].map((post, postIndex) => postIndex === index ? nextPost : post),
      },
    }));
  }

  function addPost(group) {
    setDraft((current) => ({
      ...current,
      posts: {
        ...current.posts,
        [group]: [
          ...current.posts[group],
          {
            title: { zh: "新文章", en: "New Post" },
            excerpt: { zh: "", en: "" },
            url: "https://blog.qiaomu.ai",
            date: new Date().toISOString().slice(0, 10),
          },
        ],
      },
    }));
  }

  function addProject() {
    setDraft((current) => ({
      ...current,
      projects: [
        ...current.projects,
        {
          id: crypto.randomUUID(),
          repoUrl: "",
          name: "New Project",
          description: { zh: "", en: "" },
          tags: [],
          featured: false,
          priority: current.projects.length + 1,
        },
      ],
    }));
  }

  function updateSupportIntro(language, value) {
    setDraft((current) => ({
      ...current,
      support: {
        ...current.support,
        intro: {
          ...current.support.intro,
          [language]: value,
        },
      },
    }));
  }

  function updateQrCode(index, nextQrCode) {
    setDraft((current) => ({
      ...current,
      support: {
        ...current.support,
        qrCodes: current.support.qrCodes.map((qr, qrIndex) => qrIndex === index ? nextQrCode : qr),
      },
    }));
  }

  function addQrCode() {
    setDraft((current) => ({
      ...current,
      support: {
        ...current.support,
        qrCodes: [
          ...(current.support.qrCodes || []),
          { id: crypto.randomUUID(), label: "Support QR", url: "" },
        ],
      },
    }));
  }

  const activeLanguage = useMemo(() => lang, [lang]);

  return (
    <div className="admin-overlay">
      <aside className="admin-panel">
        <div className="admin-head">
          <h2>{t.adminTitle}</h2>
          <button className="icon-button ghost" onClick={onClose}>×</button>
        </div>
        {!authed ? (
          <form className="admin-login" onSubmit={login}>
            <label>
              {t.password}
              <input type="password" value={password} onChange={(event) => setPassword(event.target.value)} />
            </label>
            <button className="button primary" type="submit">{t.login}</button>
            {message && <p className="admin-message">{message}</p>}
          </form>
        ) : (
          <div className="admin-content">
            <section>
              <h3>Profile</h3>
              <label>{t.name}<input value={draft.profile.name} onChange={(event) => updateProfile("name", event.target.value)} /></label>
              <label>Tagline ZH<textarea value={draft.profile.tagline.zh} onChange={(event) => updateProfileText("tagline", "zh", event.target.value)} /></label>
              <label>Tagline EN<textarea value={draft.profile.tagline.en} onChange={(event) => updateProfileText("tagline", "en", event.target.value)} /></label>
              <label>Bio {activeLanguage.toUpperCase()}<textarea value={draft.profile.bio[activeLanguage]} onChange={(event) => updateProfileText("bio", activeLanguage, event.target.value)} /></label>
            </section>
            <section>
              <h3>{t.links}</h3>
              {draft.links.map((link, index) => (
                <div className="admin-card compact" key={link.id || index}>
                  <label>Label<input value={link.label || ""} onChange={(event) => updateLink(index, { ...link, label: event.target.value })} /></label>
                  <label>URL<input value={link.url || ""} onChange={(event) => updateLink(index, { ...link, url: event.target.value })} /></label>
                  <label className="inline-check">
                    <input type="checkbox" checked={link.visible !== false} onChange={(event) => updateLink(index, { ...link, visible: event.target.checked })} />
                    Visible
                  </label>
                </div>
              ))}
            </section>
            <section>
              <div className="admin-row">
                <h3>{t.posts}</h3>
                <div className="admin-row tight">
                  <button className="small-button" onClick={() => addPost("featured")}>{t.addPost} Featured</button>
                  <button className="small-button" onClick={() => addPost("latest")}>{t.addPost} Latest</button>
                </div>
              </div>
              {["featured", "latest"].map((group) => (
                <div className="admin-card" key={group}>
                  <h4>{group === "featured" ? t.featuredPost : t.latestPost}</h4>
                  {draft.posts[group].map((post, index) => (
                    <div className="admin-card compact" key={`${group}-${index}`}>
                      <label>Title ZH<input value={localText(post.title, "zh")} onChange={(event) => updatePost(group, index, { ...post, title: { ...(typeof post.title === "object" ? post.title : {}), zh: event.target.value } })} /></label>
                      <label>Title EN<input value={localText(post.title, "en")} onChange={(event) => updatePost(group, index, { ...post, title: { ...(typeof post.title === "object" ? post.title : {}), en: event.target.value } })} /></label>
                      <label>URL<input value={post.url || ""} onChange={(event) => updatePost(group, index, { ...post, url: event.target.value })} /></label>
                      <label>Date<input value={post.date || ""} onChange={(event) => updatePost(group, index, { ...post, date: event.target.value })} /></label>
                    </div>
                  ))}
                </div>
              ))}
            </section>
            <section>
              <div className="admin-row">
                <h3>{t.projects}</h3>
                <button className="small-button" onClick={addProject}>{t.addProject}</button>
              </div>
              {draft.projects.map((project, index) => (
                <div className="admin-card" key={project.id || index}>
                  <label>{t.repoUrl}<input value={project.repoUrl || ""} onChange={(event) => updateProject(index, { ...project, repoUrl: event.target.value })} /></label>
                  <button className="small-button" onClick={() => fetchRepo(index)}>{t.fetchRepo}</button>
                  <label>{t.name}<input value={project.name || ""} onChange={(event) => updateProject(index, { ...project, name: event.target.value })} /></label>
                  <label>{t.descZh}<textarea value={project.description?.zh || ""} onChange={(event) => updateProject(index, { ...project, description: { ...project.description, zh: event.target.value } })} /></label>
                  <label>{t.descEn}<textarea value={project.description?.en || ""} onChange={(event) => updateProject(index, { ...project, description: { ...project.description, en: event.target.value } })} /></label>
                </div>
              ))}
            </section>
            <section>
              <h3>{t.support}</h3>
              <label>{t.supportIntro} ZH<textarea value={draft.support.intro.zh} onChange={(event) => updateSupportIntro("zh", event.target.value)} /></label>
              <label>{t.supportIntro} EN<textarea value={draft.support.intro.en} onChange={(event) => updateSupportIntro("en", event.target.value)} /></label>
              <label>Buy Me a Coffee<input value={draft.support.buyMeCoffeeUrl || ""} onChange={(event) => setDraft((current) => ({ ...current, support: { ...current.support, buyMeCoffeeUrl: event.target.value } }))} /></label>
              <div className="admin-row">
                <h4>QR Codes</h4>
                <button className="small-button" onClick={addQrCode}>Add QR</button>
              </div>
              {(draft.support.qrCodes || []).map((qr, index) => (
                <div className="admin-card compact" key={qr.id || index}>
                  <label>Label<input value={qr.label || ""} onChange={(event) => updateQrCode(index, { ...qr, label: event.target.value })} /></label>
                  <label>Image URL<input value={qr.url || ""} onChange={(event) => updateQrCode(index, { ...qr, url: event.target.value })} /></label>
                  <label>Upload Image<input type="file" accept="image/*" onChange={(event) => uploadQr(event, index)} /></label>
                </div>
              ))}
            </section>
            <div className="admin-actions">
              <button className="button primary" onClick={save}>{t.save}</button>
              <button className="button" onClick={() => {
                sessionStorage.removeItem("qiaomu-admin-password");
                setAuthed(false);
              }}>{t.logout}</button>
              {message && <p className="admin-message">{message}</p>}
            </div>
          </div>
        )}
      </aside>
    </div>
  );
}

createRoot(document.getElementById("root")).render(<App />);
